import React from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ProfileChipBadge } from '../page/ProfileChipBadge';
import { profileDownloadUrl } from '../profileAssets';
import type { ProfileChip, ProfileChipTone } from '../profileChips';
import { profileMessages as m } from '../profileMessages';
import type { ProfileCardAction, ProfileCardChip, ProfileCardTile as Tile } from './profileCardModel';
import { ProfileCardFrame } from './ProfileCardFrame';
import { TILE_TEXT } from './profileCardText';
import { profileEntryMessages as e } from './profileEntryMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Profile entry card; each part uses its own slice of it */
import styles from './ProfileEntryCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ACTION_TEXT: Readonly<Record<ProfileCardAction['kind'], MessageDescriptor>> = {
  sendSpotify: e.sendToSpotify,
  connectSpotify: e.connectSpotify,
  reconnectSpotify: e.signInAgain,
  upgradeSpotify: e.upgradeToSend,
  iSetIt: m.iSetIt,
  make: e.make,
  retry: m.tryAgain,
  openKind: m.tryAgain,
  addPhoto: e.addPhoto,
  download: m.download,
  copyShort: m.copyShort,
  writeBio: m.writeForMe,
};

/** The label of an action: "Copied" for a moment after a copy; "Make" for a tool that makes the first file. */
function actionText(action: ProfileCardAction, copied: boolean): MessageDescriptor {
  if (action.kind === 'copyShort' && copied) {
    return m.copied;
  }
  if (action.kind === 'openKind' && action.reason === 'make') {
    return e.make;
  }
  return ACTION_TEXT[action.kind];
}

/** The actions that start something new: the violet "go" look (design `pt-act go`). */
const GO_ACTIONS: ReadonlySet<ProfileCardAction['kind']> = new Set(['make', 'addPhoto', 'writeBio']);

const BIO_CHIP_TEXT = { bioSaved: m.chipSaved, bioEmpty: m.chipEmpty, bioOnSpotify: m.chipOnSpotify } as const;

type BioChipKey = keyof typeof BIO_CHIP_TEXT;

function isBioChipKey(key: string): key is BioChipKey {
  return key in BIO_CHIP_TEXT;
}

function chipBadge(chip: ProfileCardChip): ProfileChip | { tone: ProfileChipTone; text: MessageDescriptor } {
  if (isBioChipKey(chip.key)) {
    return { tone: chip.tone, text: BIO_CHIP_TEXT[chip.key] };
  }
  return { key: chip.key, tone: chip.tone };
}

interface ProfileCardTileProps {
  tile: Tile;
  ghostSrc: string | null;
  busy: boolean;
  copied: boolean;
  onAction: (action: ProfileCardAction) => void;
  onOpen: () => void;
}

type TileStyle = React.CSSProperties & { '--grow': number; '--count': number };

/** One tile of the card (design profile-card a, `pt`): the frames, the name and size, the chip, the quick action. */
const ProfileCardTileComponent: React.FC<ProfileCardTileProps> = (props) => {
  const { tile, ghostSrc, busy, copied, onAction, onOpen } = props;
  const intl = useIntl();
  const text = TILE_TEXT[tile.key];
  const name = intl.formatMessage(text.name);
  const style: TileStyle = {
    '--grow': tile.frames.reduce((sum, frame) => sum + frame.aspect, 0),
    '--count': tile.frames.length,
  };
  const { action } = tile;
  const actionClass = clsx(
    styles.action,
    action !== null && GO_ACTIONS.has(action.kind) && styles.actionGo,
    action?.kind === 'sendSpotify' && styles.actionSpotify
  );
  const download = action?.kind === 'download' ? profileDownloadUrl(action.asset) : null;

  return (
    <div className={styles.tile} style={style} data-testid={`profile-card-tile-${tile.key}`}>
      <div className={styles.frames}>
        {tile.frames.map((frame, index) => (
          <ProfileCardFrame
            // The frames of a tile never change order.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            frame={frame}
            ghostSrc={ghostSrc}
            label={frame.content.type === 'bioEmpty' ? intl.formatMessage(e.bioEmpty) : name}
            onOpen={onOpen}
            testId={`profile-card-frame-${tile.key}-${index}`}
          />
        ))}
      </div>
      <div className={styles.meta}>
        <div className={styles.nameRow}>
          <b className={styles.name}>{name}</b>
          <span className={styles.size}>{intl.formatMessage(text.size)}</span>
        </div>
        <div>
          <ProfileChipBadge chip={chipBadge(tile.chip)} testId={`profile-card-chip-${tile.key}`} />
        </div>
        {download !== null && (
          <a className={actionClass} href={download} download data-testid={`profile-card-action-${tile.key}`}>
            {intl.formatMessage(m.download)}
          </a>
        )}
        {action !== null && download === null && (
          <button
            type="button"
            className={actionClass}
            disabled={busy}
            onClick={() => onAction(action)}
            data-testid={`profile-card-action-${tile.key}`}
          >
            {action.kind === 'sendSpotify' && <i className={styles.spotifyDot} aria-hidden="true" />}
            {intl.formatMessage(actionText(action, copied))}
          </button>
        )}
      </div>
    </div>
  );
};

export const ProfileCardTile = TypedMemo(ProfileCardTileComponent);
